import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as QueryKeys from "data";
import { PlanInfo, ProductIcon } from "data/models";
import { fetchPlans } from "data/queries";
import { CircleCheck, Info, PlusCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

interface FeatureCellProps {
  icon?: ProductIcon;
  value?: React.ReactNode;
  description?: string;
}

const getIcon = (iconType: ProductIcon) => {
  const theme = useTheme();
  if (iconType === "info") {
    return <Info style={{ color: theme.palette.info.main }} />;
  } else if (iconType == "check") {
    return <CircleCheck style={{ color: theme.palette.success.main }} />;
  } else if (iconType == "plus") {
    return <PlusCircle style={{ color: theme.palette.success.main }} />;
  }
};

const FeatureCell = ({ icon, value, description }: FeatureCellProps) => {
  if (icon) {
    return (
      <Tooltip
        title={description || ""}
        disableHoverListener={!description}
        arrow
      >
        <Box display="flex" justifyContent="center">
          {getIcon(icon)}
        </Box>
      </Tooltip>
    );
  }
  return <Typography align="center">{value || "-"}</Typography>;
};

interface FeatureComparisonModalProps {
  open: boolean;
  onClose: () => void;
}

export default function FeatureComparisonModal({
  open,
  onClose,
}: FeatureComparisonModalProps) {
  const [categories, setCategories] = useState<Record<string, string[]>>({});

  const plansQuery = useQuery({
    queryKey: [QueryKeys.plans],
    queryFn: () => fetchPlans(),
  });

  useEffect(() => {
    if (plansQuery.isSuccess && plansQuery.data) {
      const grouped: Record<string, string[]> = {};
      plansQuery.data.products.forEach((product) => {
        product.featureBreakdown.forEach((fb) => {
          if (!grouped[fb.category]) grouped[fb.category] = [];
          if (!grouped[fb.category].includes(fb.key)) {
            grouped[fb.category].push(fb.key);
          }
        });
      });
      setCategories(grouped);
    }
  }, [plansQuery.isSuccess]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          backgroundImage: "none",
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>Feature Comparison</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 12 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ px: 2, py: 0, pb: 2 }}>
        {plansQuery.isError && (
          <Alert severity="error">
            An error occurred while fetching the features.
          </Alert>
        )}
        {plansQuery.isPending && <Skeleton height={225} />}
        {plansQuery.isSuccess && (
          <Box>
            <Grid container mt={0} spacing={2}>
              <Grid item xs={3}>
                <Typography fontWeight="bold"></Typography>
              </Grid>
              {plansQuery.data.products.map((product) => (
                <Grid item xs key={product.displayName}>
                  <Typography fontWeight="bold" align="center">
                    {product.displayName}
                  </Typography>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>

            {Object.entries(categories).map(([category, keys]) => (
              <React.Fragment key={category}>
                <Grid container mt={0} spacing={2} pl={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {category}
                    </Typography>
                  </Grid>
                </Grid>
                {keys.map((key, index) => (
                  <Grid container mt={0} spacing={2} key={index} pl={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2">{key}</Typography>
                    </Grid>
                    {plansQuery.data.products.map((product) => {
                      const match = product.featureBreakdown.find(
                        (fb) => fb.category === category && fb.key === key
                      );
                      return (
                        <Grid item xs key={product.displayName + key}>
                          <FeatureCell
                            icon={match?.icon}
                            value={match?.value}
                            description={match?.description}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                ))}
              </React.Fragment>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
